import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Badge } from '../../../ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, X, Image as ImageIcon } from 'lucide-react';
import { doctorService } from '../../../../services';
import { useOptimisticDoctors } from '../../../../hooks/useOptimisticUpdates';

const AdminDoctorEdit = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { updateDoctorOptimistic, getCachedDoctor } = useOptimisticDoctors();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    professionalTitle: 'Dr.',
    specialty: '',
    mainCategory: '',
    cities: [],
    consultationFees: '',
    rating: '',
    experience: '',
    qualifications: [],
    about: '',
    contactNumber: '',
    employeeCode: '',
    status: 'active',
    isVisitingDoctor: false,
    isHospitalDoctor: false,
    languages: []
  });

  const [availableCities, setAvailableCities] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [cityInput, setCityInput] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Load doctor data and available options
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load doctor data
        const doctorData = await doctorService.getDoctorById(doctorId);
        setDoctor(doctorData);
        
        // Set form data
        setFormData({
          name: doctorData.fullName || '',
          professionalTitle: doctorData.professionalTitle || 'Dr.',
          specialty: doctorData.specialty || '',
          mainCategory: doctorData.mainCategory || '',
          cities: doctorData.cities || [],
          consultationFees: doctorData.consultationFees || '',
          rating: doctorData.rating || '',
          experience: doctorData.experience || '',
          qualifications: doctorData.qualifications || [],
          about: doctorData.about || '',
          contactNumber: doctorData.contactNumber || '',
          employeeCode: doctorData.employeeCode || '',
          status: doctorData.status || 'active',
          isVisitingDoctor: doctorData.isVisitingDoctor || false,
          isHospitalDoctor: doctorData.isHospitalDoctor || false,
          languages: doctorData.languages || []
        });

        // Set existing image preview if available
        if (doctorData.profileImageUrl) {
          setImagePreview(doctorData.profileImageUrl);
        }

        // Load available cities and specialties
        const [citiesData, specialtiesData] = await Promise.all([
          doctorService.getDoctorCities(),
          doctorService.getDoctorSpecialties()
        ]);
        
        setAvailableCities(citiesData || []);
        setAvailableSpecialties(specialtiesData || []);
        
      } catch (error) {
        console.error('Error loading doctor data:', error);
        toast.error('Failed to load doctor data');
        navigate('/admin/doctors');
      } finally {
        setLoading(false);
      }
    };

    if (doctorId) {
      loadData();
    }
  }, [doctorId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCityToggle = (city) => {
    setFormData(prev => ({
      ...prev,
      cities: prev.cities.includes(city)
        ? prev.cities.filter(c => c !== city)
        : [...prev.cities, city]
    }));
  };

  const handleCityAdd = () => {
    const value = cityInput.trim();
    if (!value) return;
    setFormData(prev => ({
      ...prev,
      cities: prev.cities.includes(value) ? prev.cities : [...prev.cities, value]
    }));
    setCityInput('');
  };

  const handleCityRemove = (city) => {
    setFormData(prev => ({
      ...prev,
      cities: prev.cities.filter(c => c !== city)
    }));
  };

  const handleLanguageToggle = (language) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter(l => l !== language)
        : [...prev.languages, language]
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setProfileImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    // Reset to original image if it exists
    if (doctor?.profileImageUrl) {
      setImagePreview(doctor.profileImageUrl);
    } else {
      setImagePreview(null);
    }
  };


  // Qualifications handlers
  const addQualification = () => {
    setFormData(prev => ({
      ...prev,
      qualifications: [
        ...prev.qualifications,
        { degree: '' }
      ]
    }));
  };

  const updateQualificationField = (index, field, value) => {
    setFormData(prev => {
      const next = [...(Array.isArray(prev.qualifications) ? prev.qualifications : [])];
      if (!next[index]) next[index] = { degree: '' };
      next[index] = { ...next[index], [field]: value };
      return { ...prev, qualifications: next };
    });
  };

  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: (prev.qualifications || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Prepare data for submission
      const submitData = {
        fullName: formData.name,
        professionalTitle: formData.professionalTitle,
        specialty: formData.specialty,
        mainCategory: formData.mainCategory,
        cities: formData.cities,
        consultationFees: parseFloat(formData.consultationFees) || 0,
        rating: parseFloat(formData.rating) || 0,
        experience: formData.experience,
        qualifications: formData.qualifications,
        about: formData.about,
        contactNumber: formData.contactNumber,
        employeeCode: formData.employeeCode,
        status: formData.status,
        isVisitingDoctor: formData.isVisitingDoctor,
        isHospitalDoctor: formData.isHospitalDoctor,
        languages: formData.languages
      };

      // Update doctor (with image if provided)
      await doctorService.updateDoctor(doctorId, submitData, profileImage || null);

      toast.success('Doctor updated successfully!');
      navigate('/admin/doctors');
      
    } catch (error) {
      console.error('Error updating doctor:', error);
      toast.error('Failed to update doctor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading doctor data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Doctor Not Found</h1>
          <Button onClick={() => navigate('/admin/doctors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Doctors
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/doctors')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Doctors
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Edit Doctor</h1>
            <p className="text-muted-foreground">Update doctor information and settings</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Doctor Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter doctor name"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="specialty">Specialty *</Label>
                    <Select
                      value={formData.specialty}
                      onValueChange={(value) => handleInputChange('specialty', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSpecialties.map((specialty) => (
                          <SelectItem key={specialty} value={specialty}>
                            {specialty}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mainCategory">Main Category *</Label>
                    <Input
                      id="mainCategory"
                      value={formData.mainCategory}
                      onChange={(e) => handleInputChange('mainCategory', e.target.value)}
                      placeholder="e.g., Ophthalmologist"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="experience">Experience</Label>
                    <Input
                      id="experience"
                      value={formData.experience}
                      onChange={(e) => handleInputChange('experience', e.target.value)}
                      placeholder="e.g., 10+ years"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="consultationFees">Consultation Fees (₹)</Label>
                    <Input
                      id="consultationFees"
                      type="number"
                      value={formData.consultationFees}
                      onChange={(e) => handleInputChange('consultationFees', e.target.value)}
                      placeholder="Enter consultation fees"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rating">Rating</Label>
                    <Input
                      id="rating"
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={formData.rating}
                      onChange={(e) => handleInputChange('rating', e.target.value)}
                      placeholder="Enter rating (0-5)"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Qualifications</Label>
                  {(Array.isArray(formData.qualifications) ? formData.qualifications : []).map((q, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Degree</Label>
                        <Input
                          value={q.degree || ''}
                          onChange={(e) => updateQualificationField(idx, 'degree', e.target.value)}
                          placeholder="MBBS / MS / MD"
                        />
                      </div>
                      <Button type="button" variant="outline" onClick={() => removeQualification(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addQualification}>Add Qualification</Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="about">About Doctor</Label>
                  <Textarea
                    id="about"
                    value={formData.about}
                    onChange={(e) => handleInputChange('about', e.target.value)}
                    placeholder="Enter doctor's bio and experience"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cities */}
            <Card>
              <CardHeader>
                <CardTitle>Available Cities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Add a city and press Add"
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCityAdd();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleCityAdd}>Add</Button>
                </div>

                {formData.cities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.cities.map((city) => (
                      <Badge key={city} variant="secondary" className="flex items-center gap-1">
                        {city}
                        <button type="button" onClick={() => handleCityRemove(city)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {availableCities.map((city) => (
                    <Badge
                      key={city}
                      variant={formData.cities.includes(city) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => handleCityToggle(city)}
                    >
                      {city}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Languages */}
            <Card>
              <CardHeader>
                <CardTitle>Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {['English', 'Hindi', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi'].map((language) => (
                    <Badge
                      key={language}
                      variant={formData.languages.includes(language) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => handleLanguageToggle(language)}
                    >
                      {language}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Profile Image */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Image</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {imagePreview ? (
                  <div className="space-y-2">
                    <div className="relative w-full aspect-square max-w-xs rounded-lg overflow-hidden border-2 border-gray-200">
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={removeImage}
                      className="w-full"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Image
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImageIcon className="w-10 h-10 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </CardContent>
            </Card>
          </div>

        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/doctors')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminDoctorEdit;